/**
 * Таблица аккаунтов в стиле гугл-листа:
 *  - клик = выделение ячейки, драг / shift-клик = диапазон; Cmd/Ctrl/Alt+клик —
 *    добавить ячейки в разных местах (несмежное выделение); Ctrl+A = все ячейки;
 *  - чекбоксы слева выделяют строки целиком; при выбранных строках появляется
 *    кнопка «Удалить выбранные» (только админ); чекбокс в шапке — все строки;
 *  - Ctrl+C / Ctrl+X / Delete / Ctrl+V по выделению (TSV, совместимо с гугл-
 *    таблицей); вставка ОДНОГО значения в несколько ячеек размножает его по
 *    всем, где оно подходит по типу (имя модели в статус не вставится);
 *  - Cmd+Z / Ctrl+Z / Alt+Z — откат последних правок (до 50 шагов);
 *  - статус — постоянный селект в ячейке (один клик), остальное — двойной
 *    клик / Enter / начать печатать; Esc — отмена; булевы — двойной клик;
 *  - строка-«добавлялка» по кнопке страницы; ресайз колонок; сортировка по
 *    заголовку (статус — в логическом порядке) + кнопка «Сорт: статус»;
 *  - набор/ширина колонок в localStorage на юзера+экран.
 */
import { useEffect, useMemo, useRef, useState, type ClipboardEvent, type ReactNode } from 'react';
import type { Account, AccountPatch, ModelSummary, PhoneSummary, User } from '@wml-insta/shared';
import { ACCOUNT_STATUSES, ACCOUNT_TYPES } from '@wml-insta/shared';
import { api } from '../api/client.js';
import {
  ACCOUNT_COLUMNS, loadColumnWidths, loadVisibleColumns,
  saveColumnWidths, saveVisibleColumns, type ColumnDef,
} from '../lib/columns.js';
import { formatDateTime, formatDate, formatNum, reelsUrl } from '../lib/format.js';
import { loadPersisted, savePersisted } from '../lib/persist.js';
import { ColumnsToggle } from './ColumnsToggle.js';
import { ConfirmDialog } from './ConfirmDialog.js';

interface Props {
  accounts: Account[];
  me: User;
  phones: PhoneSummary[];
  models: ModelSummary[];
  tableKey: string;
  hiddenByDefault?: string[];
  defaultPhoneId?: string;
  defaultModelId?: string;
  /** Показать строку-добавлялку (управляется кнопкой «+ Добавить аккаунт» на странице). */
  draftOpen?: boolean;
  onDraftClose?: () => void;
  onPatched: (updated: Account) => void;
  onCreated?: (created: Account) => void;
  onDeleted?: (id: string) => void;
  onOpen: (account: Account) => void;
  toolbar?: ReactNode;
  onError?: (message: string) => void;
  /** Доп. колонка действий слева (после чекбоксов) — например ✓/✗ в разделе
   *  «на выключение». Ячейка вне сетки выделения. */
  rowActions?: (account: Account) => ReactNode;
}

interface EditState {
  accountId: string;
  colKey: string;
  value: string;
}

interface CellPos { r: number; c: number }

const keyOf = (r: number, c: number) => `${r}:${c}`;
const parseKey = (k: string): CellPos => {
  const [r, c] = k.split(':');
  return { r: Number(r), c: Number(c) };
};

function rectKeys(a: CellPos, b: CellPos): string[] {
  const out: string[] = [];
  for (let r = Math.min(a.r, b.r); r <= Math.max(a.r, b.r); r++) {
    for (let c = Math.min(a.c, b.c); c <= Math.max(a.c, b.c); c++) {
      out.push(keyOf(r, c));
    }
  }
  return out;
}

function cellValue(a: Account, col: ColumnDef): unknown {
  if (col.type === 'model') return a.modelName;
  if (col.type === 'phone') return a.phoneName;
  return a[col.field];
}

const STATUS_RANK = new Map<string, number>(ACCOUNT_STATUSES.map((s, i) => [s, i]));

function compare(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), 'ru');
}

/** Значение ячейки для копирования (TSV, совместимо с гугл-таблицей). */
function copyValue(a: Account, col: ColumnDef): string {
  const raw = cellValue(a, col);
  if (raw == null) return '';
  if (col.type === 'bool') return raw ? 'TRUE' : 'FALSE';
  return String(raw);
}

function parsePastedDate(v: string): string | null | undefined {
  const t = v.trim();
  if (t === '') return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  if (iso) return iso[0];
  const ru = /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/.exec(t);
  if (ru) {
    const y = ru[3]!.length === 2 ? `20${ru[3]}` : ru[3]!;
    return `${y}-${ru[2]!.padStart(2, '0')}-${ru[1]!.padStart(2, '0')}`;
  }
  return undefined; // не распознали — пропускаем ячейку
}

interface UndoEntry { id: string; fields: AccountPatch }

export function AccountsTable({
  accounts, me, phones, models, tableKey, hiddenByDefault = [],
  defaultPhoneId, defaultModelId, draftOpen = false, onDraftClose,
  onPatched, onCreated, onDeleted, onOpen, toolbar, onError, rowActions,
}: Props) {
  const isAdmin = me.role === 'admin';
  const columns = useMemo(
    () => ACCOUNT_COLUMNS.filter(
      (c) => !(c.adminOnly && !isAdmin) && !(c.onlyArchive && tableKey !== 'archive'),
    ),
    [isAdmin, tableKey],
  );

  const [visible, setVisible] = useState<Set<string>>(
    () => loadVisibleColumns(me.id, tableKey, isAdmin, hiddenByDefault),
  );
  const [widths, setWidths] = useState<Record<string, number>>(
    () => loadColumnWidths(me.id, tableKey),
  );
  // Мультисортировка: каждый клик по заголовку ДОБАВЛЯЕТ колонку в цепочку
  // (повторные клики по ней: ↑ → ↓ → убрать). Сброс — кнопка в тулбаре.
  // Сохраняется в localStorage (переживает перезагрузку и переходы).
  const [sorts, setSortsRaw] = useState<Array<{ key: string; dir: 1 | -1 }>>(() => {
    const saved = loadPersisted<Array<{ key: string; dir: 1 | -1 }>>(me.id, `sorts.${tableKey}`, []);
    return Array.isArray(saved)
      ? saved.filter((s) => s && typeof s.key === 'string' && (s.dir === 1 || s.dir === -1))
      : [];
  });
  const setSorts = (updater: (prev: Array<{ key: string; dir: 1 | -1 }>) => Array<{ key: string; dir: 1 | -1 }>) => {
    setSortsRaw((prev) => {
      const next = updater(prev);
      savePersisted(me.id, `sorts.${tableKey}`, next);
      return next;
    });
  };
  const [editing, setEditing] = useState<EditState | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [draftBusy, setDraftBusy] = useState(false);

  // Выделение ячеек — произвольное множество (Cmd/Alt+клик добавляет).
  const [selCells, setSelCells] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<CellPos | null>(null);
  const [focus, setFocus] = useState<CellPos | null>(null);
  // Выделение строк чекбоксами (для массового удаления).
  const [rowSel, setRowSel] = useState<Set<string>>(new Set());
  const [confirmDeleteRows, setConfirmDeleteRows] = useState(false);
  const [deletingRows, setDeletingRows] = useState(false);

  const wrapRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ active: boolean; start: CellPos; base: Set<string> } | null>(null);
  // Драг ЛКМ по колонке чекбоксов — выделение строк протягиванием.
  const rowDragRef = useRef<{ active: boolean; check: boolean } | null>(null);
  const widthsRef = useRef(widths);
  widthsRef.current = widths;
  // Undo-стек: шаг = набор обратных патчей (Cmd+Z / Alt+Z).
  const undoRef = useRef<UndoEntry[][]>([]);

  const shownColumns = columns.filter((c) => visible.has(c.key));

  const sorted = useMemo(() => {
    if (sorts.length === 0) return accounts;
    const active = sorts
      .map((s) => ({ ...s, col: columns.find((c) => c.key === s.key) }))
      .filter((s): s is { key: string; dir: 1 | -1; col: ColumnDef } => Boolean(s.col));
    if (active.length === 0) return accounts;
    const cmpOne = (a: Account, b: Account, s: { dir: 1 | -1; col: ColumnDef }): number => {
      if (s.col.type === 'status') {
        const rank = (x: Account) => (x.status == null ? 99 : STATUS_RANK.get(x.status) ?? 98);
        return (rank(a) - rank(b)) * s.dir;
      }
      return compare(cellValue(a, s.col), cellValue(b, s.col)) * s.dir;
    };
    return [...accounts].sort((a, b) => {
      for (const s of active) {
        const r = cmpOne(a, b, s);
        if (r !== 0) return r;
      }
      return 0;
    });
  }, [accounts, sorts, columns]);

  // Как в гугл-таблице: выделил числовые ячейки — внизу справа сумма/среднее.
  const selStats = useMemo(() => {
    if (selCells.size < 2) return null;
    const nums: number[] = [];
    for (const key of selCells) {
      const { r, c } = parseKey(key);
      const a = sorted[r];
      const col = shownColumns[c];
      if (!a || !col || col.type !== 'number') continue;
      const v = cellValue(a, col);
      if (typeof v === 'number') nums.push(v);
    }
    if (nums.length < 2) return null;
    const sum = nums.reduce((s, n) => s + n, 0);
    return { sum, avg: Math.round(sum / nums.length), count: nums.length };
  }, [selCells, sorted, shownColumns]);

  // ---- Виртуализация: рисуем только видимые строки ----
  // Высота строки фиксированная (.sheet-table td { height: 34px }), поэтому
  // окно считается арифметикой, без измерения каждой строки. До VIRT_MIN
  // строк рендерим всё как раньше — там выигрыша нет, а риск артефактов есть.
  const ROW_H = 34;
  const VIRT_MIN = 80;
  const OVERSCAN = 8;
  const [viewport, setViewport] = useState({ top: 0, height: 900 });

  useEffect(() => {
    const el = wrapRef.current?.querySelector<HTMLElement>('.table-scroll');
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      // Контейнер может скроллиться сам (десктоп) или ехать вместе со
      // страницей (узкий экран, .table-scroll без max-height) — учитываем оба.
      const top = el.scrollTop + Math.max(0, -rect.top);
      const height = Math.max(200, Math.min(el.clientHeight, window.innerHeight - Math.max(0, rect.top)));
      setViewport((prev) => (prev.top === top && prev.height === height ? prev : { top, height }));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, []);

  const virtual = sorted.length > VIRT_MIN;
  const firstRow = virtual ? Math.max(0, Math.floor(viewport.top / ROW_H) - OVERSCAN) : 0;
  const lastRow = virtual
    ? Math.min(sorted.length, Math.ceil((viewport.top + viewport.height) / ROW_H) + OVERSCAN)
    : sorted.length;
  const visibleRows = virtual ? sorted.slice(firstRow, lastRow) : sorted;
  const padTop = firstRow * ROW_H;
  const padBottom = Math.max(0, (sorted.length - lastRow) * ROW_H);
  const padCols = shownColumns.length + 1 + (rowActions ? 1 : 0);

  // Стрелками фокус может уйти за пределы отрисованного окна — подтягиваем.
  useEffect(() => {
    if (!virtual || !focus) return;
    const el = wrapRef.current?.querySelector<HTMLElement>('.table-scroll');
    if (!el || el.scrollHeight <= el.clientHeight) return;
    const rowTop = focus.r * ROW_H;
    if (rowTop < el.scrollTop) el.scrollTop = rowTop;
    else if (rowTop + ROW_H > el.scrollTop + el.clientHeight) el.scrollTop = rowTop + ROW_H - el.clientHeight;
  }, [focus?.r, virtual]);

  const accountById = useMemo(() => {
    const m = new Map<string, Account>();
    for (const a of accounts) m.set(a.id, a);
    return m;
  }, [accounts]);

  const modelByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const x of models) m.set(x.name.trim().toLowerCase(), x.id);
    return m;
  }, [models]);
  const phoneByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const x of phones) m.set(x.name.trim().toLowerCase(), x.id);
    return m;
  }, [phones]);

  useEffect(() => {
    const up = () => {
      if (dragRef.current) dragRef.current.active = false;
      if (rowDragRef.current) rowDragRef.current.active = false;
    };
    document.addEventListener('mouseup', up);
    return () => document.removeEventListener('mouseup', up);
  }, []);

  // Автоскролл при драг-выделении: тянешь к краю таблицы — она едет дальше,
  // выделение дотягивается до ячейки под курсором (elementFromPoint), иначе
  // выделить можно было бы только видимую область.
  useEffect(() => {
    const mouse = { x: 0, y: 0 };
    let raf = 0;
    const EDGE = 44;
    const step = () => {
      raf = 0;
      const dragging = dragRef.current?.active || rowDragRef.current?.active;
      const scroller = wrapRef.current?.querySelector<HTMLElement>('.table-scroll');
      if (!dragging || !scroller) return;
      const rect = scroller.getBoundingClientRect();
      let vy = 0;
      let vx = 0;
      if (mouse.y > rect.bottom - EDGE) vy = Math.min(28, (mouse.y - (rect.bottom - EDGE)) * 0.6);
      else if (mouse.y < rect.top + EDGE) vy = -Math.min(28, ((rect.top + EDGE) - mouse.y) * 0.6);
      if (mouse.x > rect.right - EDGE) vx = Math.min(24, (mouse.x - (rect.right - EDGE)) * 0.6);
      else if (mouse.x < rect.left + EDGE) vx = -Math.min(24, ((rect.left + EDGE) - mouse.x) * 0.6);
      if (vy !== 0 || vx !== 0) {
        scroller.scrollTop += vy;
        scroller.scrollLeft += vx;
        // мышь стоит у края — mouseenter не стреляет, тянем выделение вручную
        const el = document.elementFromPoint(
          Math.min(Math.max(mouse.x, rect.left + 2), rect.right - 2),
          Math.min(Math.max(mouse.y, rect.top + 2), rect.bottom - 2),
        );
        const cell = el?.closest?.('[data-cell-r]') as HTMLElement | null;
        if (cell && dragRef.current?.active) {
          dragHandlersRef.current.cellMouseEnter(Number(cell.dataset.cellR), Number(cell.dataset.cellC));
        }
        const rowCell = el?.closest?.('[data-rowsel-id]') as HTMLElement | null;
        if (rowCell && rowDragRef.current?.active) {
          dragHandlersRef.current.rowDragEnter(rowCell.dataset.rowselId!);
        }
      }
      raf = requestAnimationFrame(step);
    };
    const move = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      if ((dragRef.current?.active || rowDragRef.current?.active) && !raf) {
        raf = requestAnimationFrame(step);
      }
    };
    document.addEventListener('mousemove', move);
    return () => {
      document.removeEventListener('mousemove', move);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Клик в любое место вне таблицы сбрасывает выделение ячеек.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setSelCells(new Set());
        setAnchor(null);
        setFocus(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Смена набора строк/колонок (фильтры, сортировка) сбрасывает выделение ячеек.
  useEffect(() => {
    setSelCells(new Set());
    setAnchor(null);
    setFocus(null);
  }, [sorted.length, shownColumns.length, sorts]);

  // Чекбоксы строк живут по id — чистим отфильтрованные/удалённые.
  useEffect(() => {
    setRowSel((prev) => {
      const next = new Set<string>();
      for (const id of prev) if (accountById.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [accountById]);

  function widthOf(c: ColumnDef): number {
    return widths[c.key] ?? c.width ?? 120;
  }
  const totalWidth = 34 + shownColumns.reduce((s, c) => s + widthOf(c), 0);

  function changeVisible(next: Set<string>) {
    setVisible(next);
    saveVisibleColumns(me.id, tableKey, next);
  }

  function clickHeader(key: string) {
    setSorts((prev) => {
      const idx = prev.findIndex((s) => s.key === key);
      // Новая колонка — добавляется в конец цепочки сортировки.
      if (idx === -1) return [...prev, { key, dir: 1 }];
      // Уже в цепочке: ↑ → ↓ → убрать (остальные критерии не трогаем).
      const cur = prev[idx]!;
      if (cur.dir === 1) return prev.map((s, i) => (i === idx ? { key, dir: -1 as const } : s));
      return prev.filter((_, i) => i !== idx);
    });
  }

  function startResize(e: React.MouseEvent, colKey: string, startW: number) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const move = (ev: MouseEvent) => {
      const w = Math.max(50, Math.min(800, startW + (ev.clientX - startX)));
      setWidths((prev) => ({ ...prev, [colKey]: w }));
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      saveColumnWidths(me.id, tableKey, widthsRef.current);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }

  // ======================= PATCH + UNDO =======================

  function pushUndo(step: UndoEntry[]) {
    if (step.length === 0) return;
    undoRef.current.push(step);
    if (undoRef.current.length > 50) undoRef.current.shift();
  }

  function reverseOf(account: Account, body: AccountPatch): AccountPatch {
    const reverse: Record<string, unknown> = {};
    for (const k of Object.keys(body)) {
      reverse[k] = (account as unknown as Record<string, unknown>)[k] ?? null;
    }
    return reverse as AccountPatch;
  }

  async function patch(account: Account, body: AccountPatch, recordUndo = true) {
    if (recordUndo) pushUndo([{ id: account.id, fields: reverseOf(account, body) }]);
    setSavingId(account.id);
    try {
      const updated = await api<Account>(`/api/accounts/${account.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      onPatched(updated);
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setSavingId(null);
    }
  }

  async function applyBulk(patches: Map<string, AccountPatch>, recordUndo = true) {
    if (patches.size === 0) return;
    if (recordUndo) {
      const step: UndoEntry[] = [];
      for (const [id, body] of patches) {
        const acc = accountById.get(id);
        if (acc) step.push({ id, fields: reverseOf(acc, body) });
      }
      pushUndo(step);
    }
    setBulkBusy(true);
    let firstError: string | null = null;
    await Promise.all(
      Array.from(patches.entries()).map(async ([id, body]) => {
        try {
          const updated = await api<Account>(`/api/accounts/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
          });
          onPatched(updated);
        } catch (e) {
          if (!firstError) firstError = e instanceof Error ? e.message : 'Не удалось сохранить';
        }
      }),
    );
    setBulkBusy(false);
    if (firstError) onError?.(firstError);
  }

  async function undo() {
    const step = undoRef.current.pop();
    if (!step || bulkBusy) return;
    const patches = new Map<string, AccountPatch>(step.map((e) => [e.id, e.fields]));
    await applyBulk(patches, false);
  }

  // ======================= РЕДАКТИРОВАНИЕ ЯЧЕЙКИ =======================

  function startEdit(a: Account, col: ColumnDef, initial?: string) {
    if (!col.editable) return;
    if (col.type === 'status') return; // в ячейке постоянный селект — редактируется одним кликом
    if (col.type === 'secret' && !isAdmin) return;
    if (col.type === 'bool') {
      patch(a, { [col.field]: !a[col.field] } as AccountPatch);
      return;
    }
    const raw = col.type === 'model' ? (a.modelId ?? '')
      : col.type === 'phone' ? a.phoneId
      : a[col.field];
    setEditing({
      accountId: a.id,
      colKey: col.key,
      value: initial !== undefined ? initial : raw == null ? '' : String(raw),
    });
  }

  async function commitEdit(a: Account, col: ColumnDef, rawValue: string) {
    setEditing(null);
    const v = rawValue.trim();
    const body: AccountPatch = {};
    switch (col.type) {
      case 'number': {
        const n = v === '' ? null : Number(v.replace(/\s+/g, ''));
        if (v !== '' && (!Number.isFinite(n!) || n! < 0)) return;
        if (col.field === 'containerId') body.containerId = n == null ? null : Math.round(n);
        else (body as Record<string, unknown>)[col.field] = n == null ? 0 : Math.round(n);
        break;
      }
      case 'date':
        (body as Record<string, unknown>)[col.field] = v === '' ? null : v;
        break;
      case 'model':
        body.modelId = v === '' ? null : v;
        break;
      case 'phone':
        if (v === '') return;
        body.phoneId = v;
        break;
      default:
        if (col.field === 'username' && v === '') return;
        (body as Record<string, unknown>)[col.field] = v === '' ? null : v;
    }
    const before = col.type === 'model' ? (a.modelId ?? '') : col.type === 'phone' ? a.phoneId : a[col.field];
    const beforeStr = before == null ? '' : String(before);
    const afterStr = Object.values(body)[0] == null ? '' : String(Object.values(body)[0]);
    if (beforeStr === afterStr) return;
    await patch(a, body);
  }

  // ======================= ВЫДЕЛЕНИЕ ЯЧЕЕК =======================

  function selectAllCells() {
    if (sorted.length === 0 || shownColumns.length === 0) return;
    setSelCells(new Set(rectKeys({ r: 0, c: 0 }, { r: sorted.length - 1, c: shownColumns.length - 1 })));
    setAnchor({ r: 0, c: 0 });
    setFocus({ r: sorted.length - 1, c: shownColumns.length - 1 });
    wrapRef.current?.focus({ preventScroll: true });
  }

  function cellMouseDown(e: React.MouseEvent, r: number, c: number) {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('a, button, input, select')) return;
    e.preventDefault();
    wrapRef.current?.focus({ preventScroll: true });
    const multi = e.metaKey || e.ctrlKey || e.altKey;
    const k = keyOf(r, c);

    if (e.shiftKey && anchor) {
      const rect = rectKeys(anchor, { r, c });
      setSelCells(multi ? new Set([...selCells, ...rect]) : new Set(rect));
      setFocus({ r, c });
      return;
    }
    if (multi) {
      const next = new Set(selCells);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      setSelCells(next);
      setAnchor({ r, c });
      setFocus({ r, c });
      dragRef.current = { active: true, start: { r, c }, base: next };
      return;
    }
    setSelCells(new Set([k]));
    setAnchor({ r, c });
    setFocus({ r, c });
    dragRef.current = { active: true, start: { r, c }, base: new Set() };
  }

  // Автоскроллу (rAF-цикл в эффекте) нужны свежие хэндлеры выделения.
  const dragHandlersRef = useRef({ cellMouseEnter, rowDragEnter });
  dragHandlersRef.current = { cellMouseEnter, rowDragEnter };

  function cellMouseEnter(r: number, c: number) {
    const d = dragRef.current;
    if (!d?.active) return;
    setSelCells(new Set([...d.base, ...rectKeys(d.start, { r, c })]));
    setFocus({ r, c });
  }

  /** Собрать TSV: bounding box выделения, невыделенные ячейки — пустые. */
  function selectionTsv(): string | null {
    if (selCells.size === 0) return null;
    const cells = Array.from(selCells).map(parseKey);
    const r1 = Math.min(...cells.map((p) => p.r));
    const r2 = Math.max(...cells.map((p) => p.r));
    const c1 = Math.min(...cells.map((p) => p.c));
    const c2 = Math.max(...cells.map((p) => p.c));
    const lines: string[] = [];
    for (let r = r1; r <= r2; r++) {
      const a = sorted[r];
      const rowCells: string[] = [];
      for (let c = c1; c <= c2; c++) {
        const col = shownColumns[c];
        rowCells.push(a && col && selCells.has(keyOf(r, c)) ? copyValue(a, col) : '');
      }
      lines.push(rowCells.join('\t'));
    }
    return lines.join('\n');
  }

  /** Пустое значение для очистки ячейки; undefined = очищать нельзя. */
  function clearedValue(col: ColumnDef): unknown {
    if (!col.editable) return undefined;
    if (col.field === 'username' || col.type === 'phone') return undefined;
    if (col.type === 'secret' && !isAdmin) return undefined;
    if (col.type === 'bool') return false;
    if (col.type === 'number') return col.field === 'containerId' ? null : 0;
    return null; // текст / дата / статус / модель
  }

  /** Привести вставляемый текст к значению колонки; undefined = не подходит по типу. */
  function coercePasted(col: ColumnDef, raw: string): unknown {
    if (!col.editable) return undefined;
    if (col.type === 'secret' && !isAdmin) return undefined;
    const v = raw.trim();
    switch (col.type) {
      case 'bool': {
        if (/^(true|да|✓|1|\+)$/i.test(v)) return true;
        if (/^(false|нет|—|-|0|)$/i.test(v)) return false;
        return undefined;
      }
      case 'number': {
        if (v === '') return col.field === 'containerId' ? null : 0;
        const n = Number(v.replace(/\s+/g, '').replace(',', '.'));
        if (!Number.isFinite(n) || n < 0) return undefined;
        return Math.round(n);
      }
      case 'date':
        return parsePastedDate(v);
      case 'status': {
        if (v === '' || v === '—') return null;
        const found = ACCOUNT_STATUSES.find((s) => s.toLowerCase() === v.toLowerCase());
        return found ?? undefined;
      }
      case 'model': {
        if (v === '' || v === '—') return null;
        return modelByName.get(v.toLowerCase()) ?? undefined;
      }
      case 'phone': {
        if (v === '') return undefined;
        return phoneByName.get(v.toLowerCase()) ?? undefined;
      }
      default:
        if (col.field === 'username' && v === '') return undefined;
        return v === '' || v === '—' ? null : v;
    }
  }

  function patchField(col: ColumnDef): string {
    if (col.type === 'model') return 'modelId';
    if (col.type === 'phone') return 'phoneId';
    return col.field;
  }

  async function clearSelection() {
    if (selCells.size === 0) return;
    const patches = new Map<string, Record<string, unknown>>();
    for (const key of selCells) {
      const { r, c } = parseKey(key);
      const a = sorted[r];
      const col = shownColumns[c];
      if (!a || !col) continue;
      const cleared = clearedValue(col);
      if (cleared === undefined) continue;
      const current = col.type === 'model' ? a.modelId : (a as unknown as Record<string, unknown>)[col.field];
      if ((current == null || current === false || current === 0) && (cleared == null || cleared === false || cleared === 0)) continue;
      let body = patches.get(a.id);
      if (!body) {
        body = {};
        patches.set(a.id, body);
      }
      body[patchField(col)] = cleared;
    }
    await applyBulk(patches as Map<string, AccountPatch>);
  }

  function onCopy(e: ClipboardEvent) {
    if (editing || selCells.size === 0) return;
    if ((e.target as HTMLElement).closest('input, select, textarea')) return;
    const tsv = selectionTsv();
    if (tsv == null) return;
    e.preventDefault();
    e.clipboardData.setData('text/plain', tsv);
  }

  function onCut(e: ClipboardEvent) {
    if (editing || selCells.size === 0) return;
    if ((e.target as HTMLElement).closest('input, select, textarea')) return;
    const tsv = selectionTsv();
    if (tsv == null) return;
    e.preventDefault();
    e.clipboardData.setData('text/plain', tsv);
    clearSelection();
  }

  function onPaste(e: ClipboardEvent) {
    if (editing || selCells.size === 0) return;
    if ((e.target as HTMLElement).closest('input, select, textarea')) return;
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    e.preventDefault();

    const rows = text.replace(/\r/g, '').split('\n');
    while (rows.length && rows[rows.length - 1] === '') rows.pop();
    const isSingleValue = rows.length === 1 && !rows[0]!.includes('\t');

    // Одно значение + несколько ячеек → размножаем по всем совместимым по типу.
    if (isSingleValue && selCells.size > 1) {
      const value = rows[0]!;
      const patches = new Map<string, Record<string, unknown>>();
      for (const key of selCells) {
        const { r, c } = parseKey(key);
        const a = sorted[r];
        const col = shownColumns[c];
        if (!a || !col) continue;
        const coerced = coercePasted(col, value);
        if (coerced === undefined) continue; // не подходит по формату — пропуск
        let body = patches.get(a.id);
        if (!body) {
          body = {};
          patches.set(a.id, body);
        }
        body[patchField(col)] = coerced;
      }
      applyBulk(patches as Map<string, AccountPatch>);
      return;
    }

    // Сетка из буфера — кладём от левого-верхнего угла выделения.
    const cells = Array.from(selCells).map(parseKey);
    const r1 = Math.min(...cells.map((p) => p.r));
    const c1 = Math.min(...cells.map((p) => p.c));
    const patches = new Map<string, Record<string, unknown>>();
    const pastedKeys: string[] = [];
    for (let i = 0; i < rows.length; i++) {
      const a = sorted[r1 + i];
      if (!a) break;
      const rowVals = rows[i]!.split('\t');
      for (let j = 0; j < rowVals.length; j++) {
        const col = shownColumns[c1 + j];
        if (!col) break;
        const value = coercePasted(col, rowVals[j]!);
        if (value === undefined) continue;
        let body = patches.get(a.id);
        if (!body) {
          body = {};
          patches.set(a.id, body);
        }
        body[patchField(col)] = value;
        pastedKeys.push(keyOf(r1 + i, c1 + j));
      }
    }
    if (pastedKeys.length) setSelCells(new Set(pastedKeys));
    applyBulk(patches as Map<string, AccountPatch>);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (editing) return;
    const target = e.target as HTMLElement;
    if (target.closest('input, select, textarea')) return;

    if ((e.metaKey || e.ctrlKey || e.altKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      undo();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      selectAllCells();
      return;
    }
    if (selCells.size === 0) return;

    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      clearSelection();
      return;
    }
    if (e.key === 'Escape') {
      setSelCells(new Set());
      setAnchor(null);
      setFocus(null);
      return;
    }

    const move: Record<string, [number, number]> = {
      ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1],
    };
    if (move[e.key] && focus) {
      e.preventDefault();
      const [dr, dc] = move[e.key]!;
      const nr = Math.max(0, Math.min(sorted.length - 1, focus.r + dr));
      const nc = Math.max(0, Math.min(shownColumns.length - 1, focus.c + dc));
      const nf = { r: nr, c: nc };
      setFocus(nf);
      if (e.shiftKey && anchor) {
        setSelCells(new Set(rectKeys(anchor, nf)));
      } else {
        setAnchor(nf);
        setSelCells(new Set([keyOf(nr, nc)]));
      }
      return;
    }

    if (!focus) return;
    const a = sorted[focus.r];
    const col = shownColumns[focus.c];
    if (!a || !col) return;

    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      startEdit(a, col);
      return;
    }
    // Начать печатать = начать редактирование с этого символа (текст/числа).
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      if (col.type === 'text' || col.type === 'number' || (col.type === 'secret' && isAdmin)) {
        e.preventDefault();
        startEdit(a, col, e.key);
      }
    }
  }

  // ======================= СТРОКИ: ЧЕКБОКСЫ + УДАЛЕНИЕ =======================

  const allRowsChecked = sorted.length > 0 && sorted.every((a) => rowSel.has(a.id));

  function rowDragStart(e: React.MouseEvent, id: string) {
    if (e.button !== 0) return;
    e.preventDefault();
    const check = !rowSel.has(id);
    rowDragRef.current = { active: true, check };
    setRowSel((prev) => {
      const next = new Set(prev);
      if (check) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function rowDragEnter(id: string) {
    const d = rowDragRef.current;
    if (!d?.active) return;
    setRowSel((prev) => {
      const next = new Set(prev);
      if (d.check) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function toggleAllRows() {
    setRowSel(allRowsChecked ? new Set() : new Set(sorted.map((a) => a.id)));
  }

  async function deleteSelectedRows() {
    setDeletingRows(true);
    let firstError: string | null = null;
    for (const id of rowSel) {
      try {
        await api(`/api/accounts/${id}`, { method: 'DELETE' });
        onDeleted?.(id);
      } catch (e) {
        if (!firstError) firstError = e instanceof Error ? e.message : 'Не удалось удалить';
      }
    }
    setDeletingRows(false);
    setConfirmDeleteRows(false);
    setRowSel(new Set());
    if (firstError) onError?.(firstError);
  }

  // ======================= СТРОКА-ДОБАВЛЯЛКА =======================

  function draftValue(col: ColumnDef): string {
    if (col.type === 'phone') return draft.phoneId ?? defaultPhoneId ?? '';
    if (col.type === 'model') return draft.modelId ?? defaultModelId ?? '';
    return draft[col.field] ?? '';
  }

  function setDraftValue(col: ColumnDef, v: string) {
    const key = col.type === 'phone' ? 'phoneId' : col.type === 'model' ? 'modelId' : col.field;
    setDraft((d) => ({ ...d, [key]: v }));
  }

  const draftPhoneId = draft.phoneId ?? defaultPhoneId ?? '';
  // Страна (на русском) и язык контейнера обязательны при добавлении —
  // по стране срмка подбирает координату для создания контейнера.
  const draftReady = Boolean(
    (draft.username ?? '').trim() && draftPhoneId
    && (draft.country ?? '').trim() && (draft.language ?? '').trim(),
  );

  async function submitDraft() {
    if (!draftReady || draftBusy) return;
    setDraftBusy(true);
    try {
      const body: Record<string, unknown> = {
        phoneId: draftPhoneId,
        modelId: (draft.modelId ?? defaultModelId) || null,
      };
      for (const col of columns) {
        if (col.type === 'phone' || col.type === 'model') continue;
        const raw = draft[col.field];
        if (raw === undefined || raw === '') continue;
        if (col.type === 'bool') body[col.field] = raw === '1';
        else if (col.type === 'number') {
          const n = Number(raw.replace(/\s+/g, ''));
          if (Number.isFinite(n) && n >= 0) body[col.field] = Math.round(n);
        } else body[col.field] = raw.trim();
      }
      const created = await api<Account>('/api/accounts', { method: 'POST', body: JSON.stringify(body) });
      setDraft({});
      onCreated?.(created);
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Не удалось создать');
    } finally {
      setDraftBusy(false);
    }
  }

  function renderDraftCell(col: ColumnDef): ReactNode {
    // Нередактируемые поля (дата старта — авто при «Прогреве», парсинг) — прочерк.
    if (!col.editable) {
      return <div className="cell"><span className="muted">—</span></div>;
    }
    if (col.type === 'bool') {
      return (
        <div className="cell" style={{ justifyContent: 'flex-start' }}>
          <input
            type="checkbox"
            checked={draftValue(col) === '1'}
            onChange={(e) => setDraftValue(col, e.target.checked ? '1' : '')}
            style={{ accentColor: 'var(--accent)' }}
          />
        </div>
      );
    }
    if (col.type === 'status' || col.type === 'model' || col.type === 'phone') {
      return (
        <select
          className="new-cell-input"
          value={draftValue(col)}
          onChange={(e) => setDraftValue(col, e.target.value)}
        >
          {col.type === 'status' && (
            <>
              <option value="">—</option>
              {ACCOUNT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </>
          )}
          {col.type === 'model' && (
            <>
              <option value="">—</option>
              {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </>
          )}
          {col.type === 'phone' && (
            <>
              <option value="">— телефон —</option>
              {phones.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </>
          )}
        </select>
      );
    }
    return (
      <input
        className="new-cell-input"
        type={col.type === 'date' ? 'date' : col.type === 'number' ? 'number' : 'text'}
        placeholder={col.field === 'username' ? '+ новый акк…' : ''}
        value={draftValue(col)}
        autoFocus={col.field === 'username'}
        list={col.field === 'accountType' ? 'account-types' : undefined}
        onChange={(e) => setDraftValue(col, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submitDraft();
          if (e.key === 'Escape') onDraftClose?.();
        }}
      />
    );
  }

  // ======================= РЕНДЕР ЯЧЕЕК =======================

  function renderCell(a: Account, col: ColumnDef): ReactNode {
    const isEditing = editing && editing.accountId === a.id && editing.colKey === col.key;

    if (isEditing) {
      if (col.type === 'model' || col.type === 'phone') {
        return (
          <select
            className="cell-input"
            autoFocus
            value={editing.value}
            onChange={(e) => commitEdit(a, col, e.target.value)}
            onBlur={() => setEditing(null)}
            onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }}
          >
            {col.type === 'model' && (
              <>
                <option value="">— без модели —</option>
                {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </>
            )}
            {col.type === 'phone' && phones.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        );
      }
      return (
        <input
          className="cell-input"
          autoFocus
          type={col.type === 'date' ? 'date' : col.type === 'number' ? 'number' : 'text'}
          value={editing.value}
          list={col.field === 'accountType' ? 'account-types' : undefined}
          onChange={(e) => setEditing((s) => (s ? { ...s, value: e.target.value } : s))}
          onBlur={(e) => commitEdit(a, col, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitEdit(a, col, (e.target as HTMLInputElement).value);
            if (e.key === 'Escape') setEditing(null);
          }}
        />
      );
    }

    if (col.type === 'bool') {
      const val = Boolean(a[col.field]);
      return (
        <div className="cell cell-bool" title="Двойной клик — переключить">
          <span className={`bool-mark ${val ? 'on' : 'off'}`}>{val ? '✓' : '—'}</span>
        </div>
      );
    }

    if (col.type === 'status') {
      const s = a.status;
      return (
        <select
          className={`status-select ${s ? `status-${s.replace(' ', '_')}` : 'status-none'}`}
          value={s ?? ''}
          title="Сменить статус"
          onChange={(e) => patch(a, { status: (e.target.value || null) as Account['status'] })}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <option value="">—</option>
          {ACCOUNT_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
        </select>
      );
    }

    if (col.type === 'secret') {
      const val = a[col.field] as string | null;
      if (!isAdmin) {
        return <div className="cell"><span className="muted">{a.hasCreds ? '•••' : '—'}</span></div>;
      }
      return (
        <div className="cell">
          {val ? (
            <span className="secret-cell">
              <code>{val}</code>
              <button
                className="copy-mini"
                title="Скопировать"
                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(val).catch(() => {}); }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                </svg>
              </button>
            </span>
          ) : <span className="muted">—</span>}
        </div>
      );
    }

    if (col.field === 'username') {
      return (
        <div className="cell">
          <span className="cell-text" style={{ fontWeight: 500 }}>{a.username}</span>
          {(
            <a
              href={reelsUrl(a.igUrl, a.username)!}
              target="_blank"
              rel="noreferrer"
              className="copy-mini"
              title="Открыть в Instagram (при выделении — все выделенные акки)"
              onClick={(e) => {
                e.stopPropagation();
                // Массовое открытие: акки из выделенных ячеек + строк-чекбоксов.
                // Работает, только если сам акк в выделении (иначе — просто ссылка).
                const ids = new Set<string>(rowSel);
                for (const key of selCells) {
                  const acc = sorted[parseKey(key).r];
                  if (acc) ids.add(acc.id);
                }
                if (!ids.has(a.id) || ids.size < 2) return;
                e.preventDefault();
                for (const acc of sorted) {
                  if (ids.has(acc.id)) window.open(reelsUrl(acc.igUrl, acc.username)!, '_blank', 'noopener');
                }
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <path d="M15 3h6v6" />
                <path d="M10 14 21 3" />
              </svg>
            </a>
          )}
          <button
            className="row-open-btn"
            style={{ marginLeft: 'auto', flexShrink: 0 }}
            title="Страница аккаунта"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onOpen(a); }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      );
    }

    // Удалённые рилсы: 0 — прочерк, >0 — красным (инста удаляла контент).
    if (col.field === 'deletedReels') {
      return (
        <div className="cell cell-num" title="Удалённых инстой рилсов (метрики заморожены и учтены)">
          {a.deletedReels > 0
            ? <span className="cell-text" style={{ color: 'var(--accent)', fontWeight: 600 }}>{a.deletedReels}</span>
            : <span className="muted">—</span>}
        </div>
      );
    }

    const raw = cellValue(a, col);
    const cls = `cell ${col.type === 'number' ? 'cell-num' : ''}`;
    let text: ReactNode;
    if (raw == null || raw === '') text = <span className="muted">—</span>;
    else if (col.type === 'number') text = <span className="cell-text">{formatNum(Number(raw))}</span>;
    else if (col.type === 'date') text = <span className="cell-text">{formatDate(String(raw))}</span>;
    else if (col.type === 'datetime') text = <span className="cell-text">{raw ? formatDateTime(String(raw)) : '—'}</span>;
    else text = <span className="cell-text">{String(raw)}</span>;

    return (
      <div className={cls} title={raw == null ? undefined : String(raw)}>
        {text}
      </div>
    );
  }

  return (
    <>
      <div className="toolbar">
        {toolbar}
        <div className="spacer" />
        {isAdmin && rowSel.size > 0 && (
          <button
            className="btn-danger"
            style={{ height: 34, padding: '0 14px', fontSize: 13 }}
            onClick={() => setConfirmDeleteRows(true)}
          >
            Удалить выбранные ({rowSel.size})
          </button>
        )}
        {selCells.size > 1 && (
          <span style={{ fontSize: 12, color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>
            {selCells.size} яч. · Ctrl+C/X/V · Del · Cmd+Z
          </span>
        )}
        {sorts.length > 0 && (
          <button
            className="btn-ghost"
            style={{ fontSize: 12, whiteSpace: 'nowrap' }}
            title="Сбросить сортировку"
            onClick={() => setSorts(() => [])}
          >
            сорт: {sorts.map((s) => `${columns.find((c) => c.key === s.key)?.label ?? s.key} ${s.dir === 1 ? '↑' : '↓'}`).join(' → ')} ✕
          </button>
        )}
        <ColumnsToggle columns={columns} visible={visible} onChange={changeVisible} />
      </div>

      <datalist id="account-types">
        {ACCOUNT_TYPES.map((t) => <option key={t} value={t} />)}
      </datalist>

      <div
        className="table-card"
        ref={wrapRef}
        tabIndex={0}
        style={{ outline: 'none', opacity: bulkBusy ? 0.6 : 1 }}
        onKeyDown={onKeyDown}
        onCopy={onCopy}
        onCut={onCut}
        onPaste={onPaste}
      >
        <div className="table-scroll">
          <table className="sheet-table" style={{ tableLayout: 'fixed', width: totalWidth }}>
            <colgroup>
              <col style={{ width: 34 }} />
              {rowActions && <col style={{ width: 76 }} />}
              {shownColumns.map((c) => <col key={c.key} style={{ width: widthOf(c) }} />)}
            </colgroup>
            <thead>
              <tr>
                <th style={{ cursor: 'default' }}>
                  <input
                    type="checkbox"
                    className="select-all-chk"
                    title={allRowsChecked ? 'Снять выделение строк' : 'Выделить все строки'}
                    checked={allRowsChecked}
                    ref={(el) => { if (el) el.indeterminate = rowSel.size > 0 && !allRowsChecked; }}
                    onChange={toggleAllRows}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                {rowActions && <th style={{ cursor: 'default' }} />}
                {shownColumns.map((c) => {
                  const sIdx = sorts.findIndex((s) => s.key === c.key);
                  const s = sIdx >= 0 ? sorts[sIdx]! : null;
                  return (
                  <th
                    key={c.key}
                    title="Клик — добавить в сортировку (повторно: ↑ → ↓ → убрать)"
                    onClick={() => clickHeader(c.key)}
                  >
                    <span className="th-label">
                      {c.label}
                      {s && (
                        <span className="sort-arrow">
                          {s.dir === 1 ? '↑' : '↓'}{sorts.length > 1 ? sIdx + 1 : ''}
                        </span>
                      )}
                    </span>
                    <span
                      className="col-resizer"
                      title="Потяни, чтобы изменить ширину"
                      onMouseDown={(e) => startResize(e, c.key, widthOf(c))}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {onCreated && draftOpen && (
                <tr className="new-row">
                  <td>
                    <div className="cell" style={{ padding: '0 5px', justifyContent: 'center' }}>
                      <button
                        className="row-open-btn"
                        title={draftReady ? 'Создать аккаунт (Enter)' : 'Заполни юзернейм, телефон, страну и язык'}
                        style={draftReady ? { color: 'var(--accent)' } : undefined}
                        disabled={!draftReady || draftBusy}
                        onClick={submitDraft}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                      </button>
                    </div>
                  </td>
                  {rowActions && <td />}
                  {shownColumns.map((c) => (
                    <td key={c.key}>{renderDraftCell(c)}</td>
                  ))}
                </tr>
              )}
              {padTop > 0 && (
                <tr className="virt-pad" style={{ height: padTop }} aria-hidden>
                  <td colSpan={padCols} />
                </tr>
              )}
              {visibleRows.map((a, i) => {
                const r = firstRow + i;
                return (
                <tr key={a.id} style={savingId === a.id ? { opacity: 0.55 } : undefined} className={rowSel.has(a.id) ? 'row-checked' : ''}>
                  <td
                    data-rowsel-id={a.id}
                    onMouseDown={(e) => rowDragStart(e, a.id)}
                    onMouseEnter={() => rowDragEnter(a.id)}
                    style={{ cursor: 'pointer' }}
                    title="Клик/протяжка — выделить строки"
                  >
                    <div className="cell" style={{ padding: 0, justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        className="row-chk"
                        checked={rowSel.has(a.id)}
                        readOnly
                        style={{ pointerEvents: 'none' }}
                      />
                    </div>
                  </td>
                  {rowActions && (
                    <td onMouseDown={(e) => e.stopPropagation()}>
                      <div className="cell" style={{ padding: '0 6px', justifyContent: 'center', gap: 6 }}>
                        {rowActions(a)}
                      </div>
                    </td>
                  )}
                  {shownColumns.map((c, ci) => {
                    const inSel = selCells.has(keyOf(r, ci));
                    const isFocus = focus && focus.r === r && focus.c === ci;
                    return (
                      <td
                        key={c.key}
                        className={`${inSel ? 'td-sel' : ''} ${isFocus ? 'td-focus' : ''}`}
                        data-cell-r={r}
                        data-cell-c={ci}
                        onMouseDown={(e) => cellMouseDown(e, r, ci)}
                        onMouseEnter={() => cellMouseEnter(r, ci)}
                        onDoubleClick={() => startEdit(a, c)}
                      >
                        {renderCell(a, c)}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
              {padBottom > 0 && (
                <tr className="virt-pad" style={{ height: padBottom }} aria-hidden>
                  <td colSpan={padCols} />
                </tr>
              )}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={shownColumns.length + (rowActions ? 2 : 1)}>
                    <div className="empty-state">Нет аккаунтов под выбранные фильтры.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selStats && (
        <div
          style={{
            position: 'fixed', right: 18, bottom: 56, zIndex: 60,
            background: 'var(--bg-elev)',
            border: '1px solid var(--border-strong)', borderRadius: 10,
            padding: '8px 14px', fontSize: 12.5, color: 'var(--text-muted)',
            boxShadow: 'var(--shadow-md)', backdropFilter: 'blur(8px)',
            display: 'flex', gap: 14, whiteSpace: 'nowrap', pointerEvents: 'none',
          }}
        >
          <span>Сумма: <b style={{ color: 'var(--text)' }}>{formatNum(selStats.sum)}</b></span>
          <span>Среднее: <b style={{ color: 'var(--text)' }}>{formatNum(selStats.avg)}</b></span>
          <span>Чисел: <b style={{ color: 'var(--text)' }}>{selStats.count}</b></span>
        </div>
      )}

      {confirmDeleteRows && (
        <ConfirmDialog
          title="Удалить выбранные аккаунты?"
          message={<>Будет удалено <b>{rowSel.size}</b> акк. вместе с историей, снапшотами и рилсами. Это действие необратимо.</>}
          confirmLabel={`Удалить ${rowSel.size}`}
          danger
          busy={deletingRows}
          onConfirm={deleteSelectedRows}
          onCancel={() => setConfirmDeleteRows(false)}
        />
      )}
    </>
  );
}
